import { LocationStrategy } from '@angular/common';
import { Injectable, NgZone } from '@angular/core';
import { FirebaseRemoteConfigService } from '@core/services/common';
import { MultiMapContextService } from '@core/services/context/multimap-context.service';
import { ContextService } from '@core/services/core/context.service';
import { FamiliesHelperService } from '@core/services/helpers/families-helper.service';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { Platform } from '@ionic/angular';
import { Deeplink, formatNotificationDeeplink, isGenericHttpsOpenDirectLink, isSfmcDeeplinkEnabled, isValidDeeplink, SfmcDeeplinks, Sim } from '@models/deeplink';
import { MultiMapAccessPoint } from '@models/multimap';
import { getQueryStringParams } from '@utils/utils';
import { BehaviorSubject } from 'rxjs';
import { config, paths } from '../../../../assets/config/deeplink-config';
import { LogService } from '../../services/core/log.service';
import { TeCuidamosHelper } from '../helpers/teCuidamos-helper.service';
import { UrlHelper } from '../helpers/url-helper.service';
import { Logout } from './auth/auth.actions';
import { AuthService } from './auth/auth.service';
import { NavigationHelper } from './navigation-helper.service';
import { UiHelperService } from './ui-helper.service';



@Injectable({
  providedIn: 'root'
})
export class DeeplinkService {
  deeplinkSimulation = '/simulation/mets';
  deeplinkOpenApp = '/openapp/mets';
  deeplinkLocator = '/mapfreapp/locator';

  urlSchemeFile = 'file';
  urlSchemeContent = 'content';

  private readonly PRO_DYNAMIC_LINK_HOST = 'linksmapfre.page.link';
  private readonly PRE_DYNAMIC_LINK_HOST = 'mapfreapp.page.link';

  private pendingDeeplinkPostLogin: Deeplink;
  authNuuma$ = new BehaviorSubject<Sim>({
    nuuma: '',
    password: '',
    documentNumber: ''
  });

  constructor(
    private platform: Platform,
    private log: LogService,
    private ui: UiHelperService,
    private zone: NgZone,
    private authService: AuthService,
    private nav: NavigationHelper,
    private url: UrlHelper,
    private iab: InAppBrowser,
    private ctx: ContextService,
    private familiesHelper: FamiliesHelperService,
    private multiMapContext: MultiMapContextService,
    private teCuidamosHelper: TeCuidamosHelper,
    private firebaseConfig: FirebaseRemoteConfigService,
    private locationStrategy: LocationStrategy
  ) {}

  async init() {
    await this.platform.ready();
    if (
      !(window as any).IonicDeeplink ||
      !(window as any).IonicDeeplink.route
    ) {
      return;
    }
    (window as any).IonicDeeplink.route(
      {
        '/simulation/mets': null,
        '/openapp/mets': null,
        '/mapfreapp/locator': null
      },
      match => {
        this.processDeepLink(match);
      },
      nomatch => {
        this.onNoMatch(nomatch);
      }
    );
  }

  openSharedUserApp(app: keyof typeof config) {
    const origin = 'com.mapfre.mapfreapp';
    const uriFallback = this.platform.is('ios')
      ? 'mapfreapp://www.mapfre.com/openapp/mets'
      : '';
    this.create(
      app,
      'autologin',
      `?origin=${origin}&uriFallback=${uriFallback}`
    );
  }

  processDeepLink = deepLink => {
    this.log.i(this, `Processing deeplink: ${deepLink}`);
    if (deepLink == null) {
      return;
    }
    this.log.i(this, 'Successfully routed');
    try {
      this.log.d(this, JSON.stringify(deepLink));
    } catch (err) {}
    if (deepLink.$link) {
      switch (deepLink.$link.path) {
        case this.deeplinkSimulation:
          this.processSimulationDeeplink(deepLink);
          break;
        case this.deeplinkOpenApp:
          this.processOpenAppDeeplink(deepLink);
          break;
        case this.deeplinkLocator:
          this.processLocatorDeeplink(deepLink);
          break;
        default:
          break;
      }
    }
  }

  private onNoMatch = async (link: any) => {
    const remoteConfig = await this.firebaseConfig.getConfigValue('deeplinkConfig').catch(() => null);
    const config: { host: string, basehref: string[] } = !!remoteConfig?.asString() ? JSON.parse(remoteConfig?.asString()) : undefined;
    const linkHost = link?.$link?.host;
    let linkUrl: string;
    switch (linkHost) {
      case this.PRE_DYNAMIC_LINK_HOST:
      case this.PRO_DYNAMIC_LINK_HOST:
        const firebaseUlr = new URL(link?.$link.url);
        linkUrl = firebaseUlr.searchParams.get('link') || link?.$link.url;
        break;
      case config?.host:
        linkUrl = link?.$link.url;
        break;
      default: break;
    }
    if (linkUrl) {
      const { path, params } = this.processDynamicDeeplinkUrl(linkUrl, config?.basehref);
      if (path) {
        this.log.i(this, 'Matched Route, treat as a Firebase-origin link', path, params);
        this.zone.run(() => {
          this.nav.toNew(path, { queryParams: params });
        });
        return;
      }
    }
    try {
      this.log.i(
        this,
        'Unmatched Route, treat as a Firebase-origin link',
        JSON.stringify(link)
      );
      this.openFirebaseLink(link);
    } catch (err) { return; }
  }


  async proccessPendingDeeplinkIfExists() {
    if (this.pendingDeeplinkPostLogin) {
      const isDeeplinkOpened = await this.performOpenDeeplink(this.pendingDeeplinkPostLogin);
      this.pendingDeeplinkPostLogin = null;
      return isDeeplinkOpened;
    }
    return false;
  }

  openNotificationDeeplink(deeplink: Deeplink) {
    const formattedDeeplink = formatNotificationDeeplink(deeplink);
    this.handleInternalDeeplink(formattedDeeplink);
  }

  private handleInternalDeeplink(deeplink: Deeplink) {
    if (!isValidDeeplink(deeplink)) {
      this.log.d(this, 'Incorrect internal deeplink, missing value property');
      return;
    }

    if (isGenericHttpsOpenDirectLink(deeplink)) {
      this.processHttpsOpenDirectLink(deeplink);
      return;
    }

    this.processInternalNavigation(deeplink);
  }

  private processInternalNavigation(deeplink: Deeplink) {
    const isUserAuthenticated = this.authService.getState().isLogged;

    if (isUserAuthenticated) {
      this.performOpenDeeplink(deeplink);
    } else {
      this.log.i(this, 'Saving pending deeplink', deeplink);
      this.nav.goLoginPage(this.ctx.isAccessTypeParticular);
      this.pendingDeeplinkPostLogin = deeplink;
    }
  }

  private performOpenDeeplink(deeplink: Deeplink) {
    const url = deeplink.value;
    if (isSfmcDeeplinkEnabled(deeplink)) {
      this.log.d(this, 'Handling SFMC safe deeplink', deeplink);
      switch (url) {
        case SfmcDeeplinks.TE_CUIDAMOS:
          return this.teCuidamosHelper.openTeCuidamosFromPushNotification('tecuidamos').toPromise();

        case SfmcDeeplinks.MULTIMAP:
          this.multiMapContext.setState({
            policy: null,
            pageCanReturn: false,
            accessPoint: MultiMapAccessPoint.PUSH_NOTIFICATION
          });
          return this.nav.newRoot('multimap');

        case SfmcDeeplinks.FAMILIES:
          return this.familiesHelper.navToFamilies('root', 'menu', false).then(() => true);

        case SfmcDeeplinks.ICT_PRICING:
          return this.nav.toNew(['client-area', 'ict-pricing'], { state: {
            deeplinkOriginNavigation: true
          }});

        default:
          break;
      }
    }

    this.log.d(this, 'Handling generic deeplink', deeplink);
    return this.nav.toNew(url, { state: { deeplinkOriginNavigation: true }}).catch(() => {
      this.log.d(this, 'Redirect to login/home page due to error trying to access internal route: ', url);
      this.nav.goHomePage();
      return true;
    });
  }

  private processHttpsOpenDirectLink(deeplink: Deeplink) {
    const url = deeplink.value;
    try {
      this.url.open(url);
    } catch (error) {}
  }

  private async create(
    appName: keyof typeof config,
    pathKey: keyof typeof paths,
    queryParams = ''
  ) {
    await this.platform.ready();
    const { urlScheme, deepLinkHost } = config[appName];
    const path = paths[pathKey];
    const link = `${urlScheme}://${deepLinkHost}${path}${queryParams}`;

    this.iab.create(link, '_system');
  }

  private openFirebaseLink(nomatch: any) {
    const { $link } = nomatch || ({} as any);
    const { url } = $link || ({} as any);
    const deeplink: Deeplink = { value: url };
    if (isGenericHttpsOpenDirectLink(deeplink)) {
      this.processHttpsOpenDirectLink(deeplink);
    }
  }

  private processSimulationDeeplink = async (match: any) => {
    await this.platform.ready();
    this.log.d(this, 'Process simulation deep link');
    this.ui.presentAlert(
      'El usuario de la aplicación ha cambiado.',
      '',
      '',
      'aceptar',
      () => {
        this.executeSimulationDeeplink(match.$args);
      }
    );
  }

  private executeSimulationDeeplink = (sim: Sim) => {
    this.log.d(this, 'Execute simulation deep link');
    const isParticular = this.ctx.isAccessTypeParticular;
    this.authService.dispatch$(new Logout()).subscribe(
      () => {
        this.nav.goLoginPage(isParticular, { queryParams: { manual: true } });
      }
    );
    this.authNuuma$.next(sim);
    this.zone.run(() => {
      this.nav.newRoot('login');
    });
  }

  private processLocatorDeeplink = async (match: any) => {
    await this.platform.ready();
    this.log.d(this, 'Process locator deep link');
    this.zone.run(() => {
      this.nav.toNewWithoutState('locator');
    });
  }

  private processOpenAppDeeplink = async (match: any) => {
    await this.platform.ready();
    this.log.d(this, 'Process openapp deep link');
  }

  private processDynamicDeeplinkUrl(url: string, replacePathList: string[] = []) {
    const urlObj = new URL(url);
    const paramObj = getQueryStringParams(urlObj.search);
    const replacePath = replacePathList?.find(r => urlObj.pathname.startsWith(r));
    return { path: urlObj.pathname.replace(replacePath, ''), params: paramObj };
  }

}